import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

import statusController from '../../controllers/tickets/status.controller.js'
import priorityController from '../../controllers/tickets/priority.controller.js'
import categoryController from '../../controllers/tickets/category.controller.js'
import templateController from '../../controllers/tickets/template.controller.js'
import triggerController from '../../controllers/tickets/trigger.controller.js'

import { DEFAULT_COLOR } from '../../config/color.config.js'
import { TASK_STATUS } from '../../config/task.config.js'

import systemUtility from '../../utils/system.utility.js'

import { t } from '@/i18n'

export const useTicketSettingsStore = defineStore('ticket-settings', () => {
  const statuses = ref({ items: [], audit: null, _meta: null })
  const priorities = ref({ items: [], audit: null, _meta: null })
  const categories = ref({ items: [], audit: null, _meta: null })
  const templates = ref({ items: [], _meta: null })
  const triggers = ref({ items: [], _meta: null })

  const visibleStatuses = computed(() => statusController.sort(statuses.value.items))
  const statusOptions = computed(() => statusController.map(visibleStatuses.value))
  const orderedPriorities = computed(() => priorityController.sort(priorities.value.items))
  const priorityOptions = computed(() => priorityController.map(orderedPriorities.value))
  const userCategoryItems = computed(() => categories.value.items.filter((row) => !row.system))
  const systemCategoryItem = computed(() => categories.value.items.find((row) => row.system) ?? null)
  const sortedTemplateItems = computed(() => systemUtility.sortFirst(templates.value.items))
  const sortedTriggerItems = computed(() => systemUtility.sortFirst(triggers.value.items))
  const userTemplateItems = computed(() => templates.value.items.filter((row) => !row.systemKey))
  const categoryOptions = computed(() => categoryController.map(userCategoryItems.value))
  const templateOptions = computed(() => templateController.map(templates.value.items))
  const userTemplateOptions = computed(() => templateController.map(userTemplateItems.value))

  const kanbanColumns = computed(() =>
    visibleStatuses.value.map((s) => ({ id: s._id, label: s.title })),
  )

  const taskStatusOptions = computed(() => [
    {
      value: TASK_STATUS.OPEN,
      label: t('tickets.management.taskKanban.notCompleted'),
      color: 'var(--warning)',
    },
    {
      value: TASK_STATUS.COMPLETED,
      label: t('tickets.management.taskKanban.completed'),
      color: 'var(--success)',
    },
  ])

  const settingsAudit = computed(() => ({
    statuses: statuses.value.audit,
    priorities: priorities.value.audit,
    categories: categories.value.audit,
  }))

  const loading = computed(() => !statuses.value._meta)

  function getStatus(id) {
    return statusController.find(id, statuses.value.items)
  }

  function getPriority(id) {
    return priorityController.find(id, priorities.value.items)
  }

  function getPriorityColor(id) {
    return getPriority(id)?.color || (id ? DEFAULT_COLOR : 'transparent')
  }

  function getCategory(id) {
    return categoryController.find(id, categories.value.items)
  }

  function getCategoryColor(id) {
    return getCategory(id)?.color || (id ? DEFAULT_COLOR : 'transparent')
  }

  function getTemplate(id) {
    return templateController.find(id, templates.value.items)
  }

  function getTrigger(id) {
    return triggerController.find(id, triggers.value.items)
  }

  const hydrate = {
    all(data) {
      statuses.value = data.statuses
      priorities.value = data.priorities
      categories.value = data.categories
      templates.value = data.templates
      triggers.value = data.triggers
    },

    statuses(data) {
      statuses.value = data
    },

    priorities(data) {
      priorities.value = data
    },

    categories(data) {
      categories.value = data
    },

    templates(data) {
      templates.value = data
    },

    triggers(data) {
      triggers.value = data
    },
  }

  function reset() {
    statuses.value = { items: [], audit: null, _meta: null }
    priorities.value = { items: [], audit: null, _meta: null }
    categories.value = { items: [], audit: null, _meta: null }
    templates.value = { items: [], _meta: null }
    triggers.value = { items: [], _meta: null }
  }

  return {
    statuses,
    priorities,
    categories,
    templates,
    triggers,
    hydrate,
    reset,

    visibleStatuses,
    statusOptions,
    orderedPriorities,
    priorityOptions,
    userCategoryItems,
    systemCategoryItem,
    sortedTemplateItems,
    sortedTriggerItems,
    userTemplateItems,
    categoryOptions,
    templateOptions,
    userTemplateOptions,
    kanbanColumns,
    taskStatusOptions,
    settingsAudit,
    loading,

    getStatus,
    getPriority,
    getPriorityColor,
    getCategory,
    getCategoryColor,
    getTemplate,
    getTrigger,
  }
})
